import { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Divider, Space, Tag, Typography } from 'antd';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  ListWorkOrderItemTagsQuery,
  WorkOrderItemTag,
} from '@/core/domain/workorder/WorkOrderItemTags/WorkOrderItemTags.types';
import {
  useDeleteWorkOrderItemTag,
  useWorkOrderItemTags,
} from '@/core/domain/workorder/WorkOrderItemTags/WorkOrderItemTags.hooks';

import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

import { PageProps } from '@/types/PageProps';

import {
  InfiniteTable,
  ColumnsType,
  DeleteIcon,
  EditIcon,
} from '@/components/_common';
import AppPageHeader from '@/components/_common/AppPageHeader';
import { useDebounce, useDefaultDeleteModal, useDisclosure } from '@/hooks';
import CreateCompanyWorkorderItemTagModal from '@/components/Companies/Settings/CreateCompanyWorkorderItemTagModal';
import UpdateCompanyWorkorderItemTagModal from '@/components/Companies/Settings/UpdateCompanyWorkorderItemTagModal';

const { Title } = Typography;

function CompanyWorkorderTagsPage({ route }: PageProps) {
  const intl = useIntl();
  const { handleDelete } = useDefaultDeleteModal('companies.settings.itemTags');
  const { companyId } = useRouteMatch<{ companyId: string }>().params;

  const [createModalVisible, createModalAction] = useDisclosure();
  const [updateModalVisible, updateModalAction] = useDisclosure();

  const [selectedItemTag, setSelectedItemTag] = useState<WorkOrderItemTag>();

  const { data: company } = useCompany(+companyId);
  useUpdateBreadcrumbParam(':companyId', company?.name);

  const [queryParams, setQueryParams] = useState<
    ListWorkOrderItemTagsQuery | undefined
  >({
    order: 'ASC',
    companyId: Number(companyId),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    useWorkOrderItemTags(queryDebounce);

  const workOrderTags = flatInfiniteData(data);

  const deleteMutation = useDeleteWorkOrderItemTag();

  const handleUpdateModalVisible = (itemTag?: WorkOrderItemTag) => {
    updateModalAction.toggle();
    setSelectedItemTag(itemTag);
  };

  const columns: ColumnsType<WorkOrderItemTag> = [
    {
      title: (
        <FormattedMessage id="companies.settings.itemTags.entity.description" />
      ),
      render: (_, record) => (
        <Tag color={record.color}>{record.description}</Tag>
      ),
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="customers.list.header.actions" />,
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => handleUpdateModalVisible(record),
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() =>
              handleDelete(
                () => deleteMutation.mutateAsync(record.id),
                () => refetch(),
              )
            }
          />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <Title level={3}>
            <FormattedMessage id={route.name} />
          </Title>

          <div className="actions">
            <Button block type="primary" onClick={createModalAction.open}>
              <FormattedMessage id="commons.entity.create" />
            </Button>
          </div>
        </div>
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
        />
        <InfiniteTable<WorkOrderItemTag>
          columns={columns}
          dataSource={workOrderTags}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      </div>

      <CreateCompanyWorkorderItemTagModal
        visible={createModalVisible}
        handleVisible={createModalAction.close}
      />

      <UpdateCompanyWorkorderItemTagModal
        visible={updateModalVisible}
        handleVisible={handleUpdateModalVisible}
        workOrderItemTag={selectedItemTag}
      />
    </>
  );
}

export default CompanyWorkorderTagsPage;
